import {
  QueryKey,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { FileWithPath } from '@mantine/dropzone';
import { notifyError } from '../../utils/NotifyError.tsx';
import { ImagePair } from '../../models/PlaceService.ts';

export interface ServiceWithImages {
  id: string;
  imageIds: string[];
}

interface CreateServiceImageActionsConfig {
  useAdd: () => {
    mutateAsync: (req: {
      serviceId: string;
      file: FormData;
    }) => Promise<unknown>;
  };
  useRemove: () => {
    mutateAsync: (req: {
      serviceId: string;
      imageId: string;
    }) => Promise<unknown>;
  };
  useImages: (
    serviceId: string | undefined,
    imageIds: string[] | undefined,
  ) => UseQueryResult<ImagePair, Error>[];
  queryKey: (id: string) => QueryKey;
}

export function serviceImageActionsFactory(
  config: CreateServiceImageActionsConfig,
) {
  return function useServiceImageActions(
    service: ServiceWithImages | undefined,
    options?: { onAfterAdd?: () => void },
  ) {
    const queryClient = useQueryClient();
    const { mutateAsync: addImage } = config.useAdd();
    const { mutateAsync: removeImage } = config.useRemove();

    const imageQueries = config.useImages(service?.id, service?.imageIds);
    const isAnyImageLoading = imageQueries.some((q) => q.isLoading);

    const handleRemove = async (q: UseQueryResult<ImagePair, Error>) => {
      try {
        await removeImage({
          serviceId: service.id,
          imageId: q.data.imageId.toString().replaceAll('/', '%2F'),
        });
        // exact: true — only invalidate the service query itself, not individual
        // image sub-queries, so the deleted image's query isn't re-fetched → no 404.
        await queryClient.invalidateQueries({
          queryKey: config.queryKey(service.id),
          exact: true,
        });
      } catch (error) {
        notifyError('刪除失敗', error.message);
      }
    };

    const handleAdd = async (files: FileWithPath[]) => {
      if (files.length === 0) return;

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          await addImage({ serviceId: service.id, file: formData });
          await queryClient.invalidateQueries({
            queryKey: config.queryKey(service.id),
            exact: true,
          });
          // 等待 refetch & re-render 完成後再執行 callback（如捲動到圖片區）
          await new Promise((resolve) => setTimeout(resolve, 200));
          options?.onAfterAdd?.();
        } catch (error) {
          notifyError('上傳失敗', error.message);
        }
      }
    };

    return { imageQueries, isAnyImageLoading, handleRemove, handleAdd };
  };
}
